import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toPng } from "html-to-image";
import { Download, FlaskConical, Share2, X } from "lucide-react";
import { toast } from "sonner";
import { fmtOdds, pct, money, parlayMath, SPORT_META } from "@/lib/odds";
import { ShareButtons } from "@/components/ShareButtons";

export function SlipShareCard({ legs, wager, meta, innerRef }) {
  const m = parlayMath(legs, Number(wager) || 0);
  const locked = legs.some((l) => l.win_probability == null);
  const sports = [...new Set(legs.map((l) => l.sport))];
  return (
    <div ref={innerRef} data-testid="slip-share-card" className="relative w-full max-w-md overflow-hidden rounded-2xl border border-[#26334D] bg-[#0B0E14] p-6" style={{ backgroundImage: "radial-gradient(ellipse 80% 40% at 50% -10%, rgba(16,185,129,0.28), transparent)" }}>
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-emerald-400"><FlaskConical size={16} /><span className="font-display text-lg font-black uppercase tracking-tight text-white">The Parlay <span className="text-emerald-400">Lab</span></span></span>
        <span className="rounded-full border border-[#26334D] px-3 py-0.5 font-mono-num text-[10px] font-bold uppercase tracking-widest text-slate-300">{legs.length}-leg slip</span>
      </div>
      <p className="mt-5 text-xs uppercase tracking-widest text-slate-500">{sports.map((s) => SPORT_META[s]?.short || s).join(" · ")}</p>
      <h1 className="font-display mt-1 text-3xl font-black uppercase leading-none tracking-tight text-white" data-testid="slip-share-card-title">{meta?.title || "My parlay"}</h1>
      <ul className="mt-4 space-y-2" data-testid="slip-share-card-legs">
        {legs.map((l) => (
          <li key={l.pick_id} className="flex items-center gap-3 rounded-lg bg-[#121824] px-3 py-2">
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: SPORT_META[l.sport]?.color }} />
            <span className="min-w-0 flex-1"><p className="truncate text-sm font-semibold text-white">{l.selection}</p><p className="truncate text-[11px] text-slate-500">{l.matchup}</p></span>
            <span className="font-mono-num text-sm font-bold text-amber-400">{fmtOdds(l.odds)}</span>
            <span className="font-mono-num w-10 text-right text-xs text-emerald-400">{pct(l.win_probability)}</span>
          </li>
        ))}
      </ul>
      <div className="mt-4 grid grid-cols-3 gap-2">
        {[["Total odds", fmtOdds(m.american), "text-amber-400"], ["Model win prob", locked ? "—" : pct(m.prob), "text-emerald-400"], ["Pays", Number(wager) ? money(m.payout) : "—", "text-white"]].map(([l, v, c]) => (
          <div key={l} className="rounded-lg bg-[#121824] px-3 py-2"><p className="text-[10px] uppercase tracking-wider text-slate-500">{l}</p><p className={`font-mono-num text-xl font-bold ${c}`}>{v}</p></div>
        ))}
      </div>
      {Number(wager) > 0 && <p className="mt-2 text-[11px] text-slate-500">{money(Number(wager))} to win {money(m.profit)}{!locked && ` · fair odds ${fmtOdds(Math.round(m.prob > 0 ? (1 / m.prob >= 2 ? (1 / m.prob - 1) * 100 : -100 / (1 / m.prob - 1)) : 0))}`}</p>}
      <p className="mt-4 text-[10px] uppercase tracking-widest text-slate-600">theparlaylab · AI probability picks · results simulated</p>
    </div>
  );
}

export function SlipShareModal({ legs, wager, meta }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const m = parlayMath(legs, Number(wager) || 0);
  const download = async () => {
    try {
      const url = await toPng(ref.current, { pixelRatio: 2, cacheBust: true });
      const a = document.createElement("a");
      a.href = url;
      a.download = "parlay-lab-slip.png";
      a.click();
      toast.success("Slip card downloaded");
    } catch { toast.error("Could not render image"); }
  };
  const text = `${meta?.title || "My parlay"} — ${legs.length} legs at ${fmtOdds(m.american)}${legs.every((l) => l.win_probability != null) ? `, model win prob ${pct(m.prob)}` : ""}: ${legs.map((l) => `${l.selection} (${fmtOdds(l.odds)})`).join(" · ")}. Built in The Parlay Lab.`;
  return (
    <>
      <button data-testid="slip-share-btn" onClick={() => setOpen(true)} disabled={!legs.length} title="Share this slip as an image" className="btn-ghost !px-3"><Share2 size={15} /></button>
      {open && createPortal(
        <div data-testid="slip-share-modal" className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/80 p-4 py-8 backdrop-blur-sm sm:items-center" onClick={() => setOpen(false)}>
          <div className="fade-up flex w-full max-w-md flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
            <SlipShareCard legs={legs} wager={wager} meta={meta} innerRef={ref} />
            <div className="flex flex-wrap justify-center gap-3">
              <button data-testid="slip-share-download-btn" onClick={download} className="btn-primary"><Download size={16} /> Download PNG</button>
              <button data-testid="slip-share-close-btn" onClick={() => setOpen(false)} className="btn-ghost !px-3"><X size={16} /></button>
            </div>
            <ShareButtons text={text} url={window.location.origin} testPrefix="slip-share" />
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
